import {
  Modal,
  Button,
  Group,
  Stack,
  Checkbox,
  Radio,
  Text,
  MultiSelect,
} from "@mantine/core";
import { useState, useEffect } from "react";
import {
  StainFilters,
  StainType,
} from "../../../services/admin/stains.service";

const STAIN_TYPES: StainType[] = ["H&E", "IHC", "ISH", "Special", "Other"];

interface CptCodeOption {
  id: string;
  code: string;
  description: string;
}

interface StainFilterModalProps {
  opened: boolean;
  onClose: () => void;
  filters: StainFilters;
  onApply: (filters: StainFilters) => void;
  cptCodes: CptCodeOption[];
}

export default function StainFilterModal({
  opened,
  onClose,
  filters,
  onApply,
  cptCodes,
}: StainFilterModalProps) {
  const [tempFilters, setTempFilters] = useState<StainFilters>(filters);

  // Sync temp filters when modal opens with new filters
  useEffect(() => {
    if (opened) {
      setTempFilters(filters);
    }
  }, [opened, filters]);

  const handleApply = () => {
    onApply(tempFilters);
    onClose();
  };

  const handleClearAll = () => {
    setTempFilters({});
  };

  const handleStainTypeChange = (type: StainType, checked: boolean) => {
    const currentTypes = tempFilters.stainTypes || [];
    const newTypes = checked
      ? [...currentTypes, type]
      : currentTypes.filter((t) => t !== type);

    setTempFilters({
      ...tempFilters,
      stainTypes: newTypes.length > 0 ? newTypes : undefined,
    });
  };

  const isStainTypeChecked = (type: StainType) => {
    return tempFilters.stainTypes?.includes(type) ?? false;
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Filter Stains"
      size="md"
      centered
      closeOnClickOutside={false}
    >
      <Stack gap="lg">
        {/* Stain Type - Multi-select checkboxes */}
        <Stack gap="xs">
          <Text fw={500} size="sm">
            Stain Type
          </Text>
          <Group>
            {STAIN_TYPES.map((type) => (
              <Checkbox
                key={type}
                label={type}
                checked={isStainTypeChecked(type)}
                onChange={(e) =>
                  handleStainTypeChange(type, e.currentTarget.checked)
                }
              />
            ))}
          </Group>
        </Stack>

        {/* Availability - Radio buttons */}
        <Radio.Group
          label="Availability"
          value={tempFilters.availability || "all"}
          onChange={(value) =>
            setTempFilters({
              ...tempFilters,
              availability:
                value === "all" ? undefined : (value as "inHouse" | "external"),
            })
          }
        >
          <Group mt="xs">
            <Radio value="all" label="All" />
            <Radio value="inHouse" label="In-House Only" />
            <Radio value="external" label="External Only" />
          </Group>
        </Radio.Group>

        {/* Status - Radio buttons */}
        <Radio.Group
          label="Status"
          value={tempFilters.status || "all"}
          onChange={(value) =>
            setTempFilters({
              ...tempFilters,
              status:
                value === "all" ? undefined : (value as "active" | "inactive"),
            })
          }
        >
          <Group mt="xs">
            <Radio value="all" label="All" />
            <Radio value="active" label="Active Only" />
            <Radio value="inactive" label="Inactive Only" />
          </Group>
        </Radio.Group>

        {/* CPT Code - Multi-select */}
        <Stack gap="xs">
          <MultiSelect
            label="CPT Code"
            placeholder="Select CPT codes to filter"
            data={cptCodes.map((cpt) => ({
              value: cpt.id,
              label: `${cpt.code} - ${cpt.description}`,
            }))}
            value={tempFilters.cptCodeIds || []}
            onChange={(values) =>
              setTempFilters({
                ...tempFilters,
                cptCodeIds: values.length > 0 ? values : undefined,
              })
            }
            searchable
            clearable
            maxDropdownHeight={200}
          />
          <Checkbox
            label="Include stains without CPT code"
            checked={tempFilters.showWithoutCpt ?? false}
            onChange={(e) =>
              setTempFilters({
                ...tempFilters,
                showWithoutCpt: e.currentTarget.checked || undefined,
              })
            }
          />
        </Stack>

        {/* Action buttons */}
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClearAll}>
            Clear All
          </Button>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApply}>Apply Filters</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
